// Section 19 and 14: Trade caravan. 8% per season, Summer and Autumn bias. A caravan offers two
// exchange rates from a barter table ("10 wood for 6 food"; no currency at v1). Choices: take the
// first, take the second, or decline. A trade gives what is on hand, up to the offer, and gets
// the same share of the return. Constants: config.events['trade-caravan'].
import type { Config } from '../../config';
import {
  choiceOutcome,
  eventEntry,
  eventPrompt,
  eventRng,
  promptNumber,
  promptString,
} from '../../core/content-helpers';
import { defineEvent, noModifiers, type EventChoiceDef } from '../../core/registry';
import type { Delta, EventPrompt, ReadContext, Resource } from '../../core/types';

type Deal = { give: Resource; giveAmount: number; get: Resource; getAmount: number };

const RESOURCES: readonly string[] = [
  'food',
  'water',
  'wood',
  'stone',
  'tools',
  'medicine',
  'cloth',
  'fibre',
];

function asResource(s: string): Resource | null {
  return RESOURCES.includes(s) ? (s as Resource) : null;
}

function offers(ctx: ReadContext, deals: readonly Deal[], count: number): Deal[] {
  const rng = eventRng(ctx, 'trade-caravan', 'offers');
  return rng.shuffle(deals).slice(0, count);
}

function dealOf(prompt: EventPrompt, n: number): Deal | null {
  const give = asResource(promptString(prompt, `give${n}`));
  const get = asResource(promptString(prompt, `get${n}`));
  if (give === null || get === null) return null;
  return {
    give,
    get,
    giveAmount: promptNumber(prompt, `giveAmount${n}`),
    getAmount: promptNumber(prompt, `getAmount${n}`),
  };
}

const round1 = (x: number): number => Math.round(x * 10) / 10;

function trade(n: number, id: string): EventChoiceDef {
  return {
    id,
    label: n === 1 ? 'Take the first offer' : 'Take the second offer',
    preview: (ctx, prompt) => {
      const deal = dealOf(prompt, n);
      if (deal === null) return [];
      return [
        {
          key: 'trade',
          params: {
            give: deal.give,
            giveAmount: deal.giveAmount,
            get: deal.get,
            getAmount: deal.getAmount,
            have: round1(ctx.state.stocks[deal.give].amount),
          },
        },
      ];
    },
    apply: (ctx, prompt) => {
      const deal = dealOf(prompt, n);
      if (deal === null || deal.giveAmount <= 0) return [choiceOutcome(prompt, id)];
      const given = Math.min(deal.giveAmount, ctx.state.stocks[deal.give].amount);
      const got = (deal.getAmount * given) / deal.giveAmount;
      const out: Delta[] = [
        { kind: 'stock', resource: deal.give, amount: -given, flow: 'other' },
        { kind: 'stock', resource: deal.get, amount: got, flow: 'other' },
      ];
      out.push(
        choiceOutcome(prompt, id, {
          give: deal.give,
          giveAmount: round1(given),
          get: deal.get,
          getAmount: round1(got),
        }),
      );
      return out;
    },
  };
}

export function tradeCaravan(config: Config) {
  const c = config.events['trade-caravan'];
  const choices: EventChoiceDef[] = [
    trade(1, 'trade-first'),
    trade(2, 'trade-second'),
    {
      id: 'decline',
      label: 'Decline',
      preview: null,
      apply: (_ctx, prompt) => [choiceOutcome(prompt, 'decline')],
    },
  ];
  const paramsOf = (ctx: ReadContext): Record<string, string | number> => {
    const params: Record<string, string | number> = {};
    offers(ctx, c.deals, c.offers).forEach((d, i) => {
      params[`give${i + 1}`] = d.give;
      params[`giveAmount${i + 1}`] = d.giveAmount;
      params[`get${i + 1}`] = d.get;
      params[`getAmount${i + 1}`] = d.getAmount;
    });
    return params;
  };
  return defineEvent({
    id: 'trade-caravan',
    label: 'Trade caravan',
    section: 'S19',
    baseWeight: c.baseChance,
    seasons: ['summer', 'autumn'],
    foreshadow: null,
    ledgerTag: null,
    weight: () => c.baseChance,
    text: (ctx) => eventEntry('trade-caravan', paramsOf(ctx)),
    apply: (ctx) => [
      eventPrompt(ctx, 'trade-caravan', choices, {
        params: paramsOf(ctx),
        defaultChoiceId: 'decline',
      }),
    ],
    choices,
    modifierTargets: [],
    modifiers: noModifiers,
  });
}
